import type { BrowserOptions } from '@sentry/browser';
import { stripPIIData } from '@jwp/ott-common/src/utils/pii';

export function getSentryOptions(dsn: string, environment: string, version: string | undefined): BrowserOptions {
  return {
    dsn,
    environment,
    release: version,
    // Unhandled error is triggered by the InPlayer WebSocket retry mechanism and clutters Sentry
    ignoreErrors: ['Unhandled error. (undefined)', 'Share canceled', 'Share() is already in progress', 'Abort due to cancellation of share.'],
    beforeBreadcrumb(breadcrumb) {
      // prevent sending console.log messages as this may contain PII-sensitive data
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }

      breadcrumb.data = stripPIIData(breadcrumb.data);
      return breadcrumb;
    },
    beforeSend(event) {
      // ignore sending events when the page is being reloaded by the service worker
      if (window.__ottServiceWorkerRefreshInProgress || window.__ottPageUnloadInProgress) {
        return null;
      }

      if (event.user) {
        event.user = stripPIIData(event.user);
      }

      return event;
    },
  };
}

export function addUnhandledRejectionsListener(captureException: (exception: unknown) => void) {
  // This catches unhandled promise rejections before sending them to Sentry. When the unhandled promise rejection is
  // not an error object, Sentry doesn't handle it very well and only shows a generic message. By creating a fake error when
  // this happens, we ensure that we receive a readable error message. The stack trace is unfortunately lost.
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (!(error instanceof Error)) {
      const wrapped = new Error(error?.message || JSON.stringify(error));
      // @ts-expect-error original is untyped, but useful in Sentry
      wrapped.original = error;

      captureException(wrapped);
      event.preventDefault();
    }
  });
}
